import calculateDownPayment from '../calculateDownPayment/calculateDownPayment.js';

/**
 * Calculates the monthly finance payment for a user based on the listing price and user payment details.
 *
 * @param {number} price - The listing price of the item being financed.
 * @param {Object} paymentDetails - An object containing user payment information.
 * @param {Object} paymentDetails.tradeIn - Details about the trade-in, if applicable.
 * @param {number} [paymentDetails.tradeIn.amountOwed=0] - The amount owed on the trade-in.
 * @param {number} [paymentDetails.tradeIn.value=0] - The value of the trade-in.
 * @param {number} [paymentDetails.loanTerm=0] - The loan term in months.
 * @param {number|null} [paymentDetails.totalEquity=null] - The total equity, if provided.
 * @param {number} [paymentDetails.taxesFees=0] - Additional taxes and fees to be included in the calculation.
 * @param {number} interestRate - The annual interest rate as a percentage (e.g., 5 for 5%).
 * @returns {number} The calculated monthly finance payment. Returns 0 if the principal is less than 0.
 */
function calculatePersonalPayment(price, paymentDetails, interestRate) {
  const {
    tradeIn = {},
    loanTerm = 0,
    totalEquity = null,
    taxesFees = 0
  } = paymentDetails;
  const {
    amountOwed = 0,
    value: tradeInValue = 0
  } = tradeIn;
  const monthlyRate = interestRate / 1200;
  const calculatedDownPayment = calculateDownPayment(price, paymentDetails);
  const equityCalc = totalEquity || tradeInValue - amountOwed;
  const principal = price - calculatedDownPayment - equityCalc + taxesFees;
  if (principal < 0) {
    return 0;
  }
  if (monthlyRate > 0) {
    return principal * monthlyRate / (1 - 1 / (1 + monthlyRate) ** loanTerm);
  }
  return principal / loanTerm;
}
export { calculatePersonalPayment as default };
